/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
import React, { useState, useEffect } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { formatISO, startOfDay, endOfDay } from 'date-fns';

import { Loader } from '@components/screen/Layout';
import SociollaCard from '@components/sociolla/SociollaCard';
import SociollaText from '@components/sociolla/SociollaText';
import { NoTranslate } from '@components/shared/TranslationContext';
import DatePicker from '@components/form/DatePicker';
import Highcharts from '@components/chart/Highcharts';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';

// Carrier pie colour palette — cycles for as many carriers as needed
const CARRIER_COLORS = [
  '#00B5DF',
  '#7D828F',
  '#FCB21E',
  '#EA1A65',
  '#2ECC71',
  '#9B59B6',
  '#E67E22',
  '#1ABC9C',
];

// Status config — label, value-colour per Figma spec
const STATUSES = [
  { id: 'draft', label: 'Draft', color: '#7C838F' },
  { id: 'need_approval', label: 'Need Approval', color: '#FCB21E' },
  { id: 'approved', label: 'Approved', color: '#2ECC71' },
  { id: 'rejected', label: 'Rejected', color: '#EA1A65' },
  { id: 'completed', label: 'Completed', color: '#00B5DF' },
];

const TOTAL_CUSTOMER_COLOR = '#0028E2';

// Default period = current month (matches BE default when no filter is sent)
const getCurrentMonthRange = () => {
  const now = new Date();
  return {
    from: formatISO(startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))),
    to: formatISO(endOfDay(now)),
  };
};

const CARRIER_TABLE_COLS = [
  { key: 'draft', label: 'Draft', color: '#7C838F' },
  { key: 'need_approval', label: 'Need Approval', color: '#FCB21E' },
  { key: 'approved', label: 'Approved', color: '#2ECC71' },
  { key: 'rejected', label: 'Rejected', color: '#EA1A65' },
  { key: 'completed', label: 'Completed', color: '#00B5DF' },
  { key: 'count', label: 'Total Orders', color: '#242B38' },
  { key: 'customer_count', label: 'Total Customers', color: '#0028E2' },
];

const GRNDashboard = () => {
  const userData = useSelector((state) => state.auth.user);
  const navigation = useNavigation();
  const is3pl = userData?.role?.code === '3pl';

  const [summary, setSummary] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [activeStatus, setActiveStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  const initialRange = getCurrentMonthRange();
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);

  const getLpbSummaryApi = useApi(MsOrder.getLpbSummary);

  const buildDateParams = (from, to) => {
    const params = {};
    if (from) {
      params.from_date = { $gte: from };
    }
    if (to) {
      params.to_date = { $lte: to };
    }
    return params;
  };

  // Redirect period: selected range, else current month (matches BE default)
  const getRedirectPeriod = () => {
    if (fromDate || toDate) {
      return buildDateParams(fromDate, toDate);
    }
    const { from, to } = getCurrentMonthRange();
    return buildDateParams(from, to);
  };

  const goToGrnList = (statusId) => {
    navigation.navigate('LPBNavigator', {
      screen: 'LPBScreen',
      params: {
        presetFilter: { status: statusId, ...getRedirectPeriod() },
      },
    });
  };

  const fetchSummary = async (filterParams = {}) => {
    setLoading(true);
    try {
      const res = await getLpbSummaryApi.request({ filter: filterParams });
      const payload = res?.data?.data;
      if (payload) {
        setSummary(payload);
        setFilteredData({
          carrier_breakdown: payload.carrier_breakdown || [],
          total_customers: payload.total_customers || 0,
        });
        setActiveStatus(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFiltered = async (status, filterParams = {}) => {
    setFilterLoading(true);
    try {
      const res = await getLpbSummaryApi.request({
        filter: { ...filterParams, status },
      });
      const payload = res?.data?.data;
      if (payload) {
        setFilteredData({
          carrier_breakdown: payload.carrier_breakdown || [],
          total_customers: payload.total_customers || 0,
        });
      }
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary(buildDateParams(fromDate, toDate));
  }, []);

  const onApplyFilter = () => {
    const params = buildDateParams(fromDate, toDate);
    fetchSummary(params);
  };

  const onResetFilter = () => {
    const { from, to } = getCurrentMonthRange();
    setFromDate(from);
    setToDate(to);
    fetchSummary(buildDateParams(from, to));
  };

  const onStatusPress = (statusId) => {
    const dateParams = buildDateParams(fromDate, toDate);
    if (statusId === 'all') {
      setActiveStatus(null);
      setFilteredData({
        carrier_breakdown: summary?.carrier_breakdown || [],
        total_customers: summary?.total_customers || 0,
      });
      return;
    }
    if (activeStatus === statusId) {
      setActiveStatus(null);
      setFilteredData({
        carrier_breakdown: summary?.carrier_breakdown || [],
        total_customers: summary?.total_customers || 0,
      });
    } else {
      setActiveStatus(statusId);
      fetchFiltered(statusId, dateParams);
    }
  };

  const getStatusCount = (statusId) => {
    if (!summary?.status_breakdown) {
      return 0;
    }
    const found = summary.status_breakdown.find((s) => s._id === statusId);
    return found ? found.count : 0;
  };

  // Build pie data for carrier distribution (Internal POV) — top 9 only
  const buildCarrierPieData = () => {
    const items = (filteredData?.carrier_breakdown || []).slice(0, 9);
    if (!items.length) {
      return [];
    }
    return [
      {
        name: 'Carriers',
        data: items.map((c) => ({
          name: c._id?.carrier_name || c._id?.carrier_id || '-',
          y: c.count || 0,
          percentage_value: c.percentage || 0,
        })),
      },
    ];
  };

  // Build pie data for status distribution (External POV)
  const buildStatusPieData = () => {
    if (!summary?.status_breakdown) {
      return [];
    }
    const items = summary.status_breakdown;
    return [
      {
        name: 'Status',
        data: items.map((s) => {
          const cfg = STATUSES.find((x) => x.id === s._id);
          return {
            name: cfg ? cfg.label : s._id,
            y: s.count || 0,
            percentage_value: s.percentage || 0,
          };
        }),
      },
    ];
  };

  const pieColors = STATUSES.map((s) => s.color);

  if (loading) {
    return <Loader />;
  }
  if (!summary) {
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* Filter section — shared between External & Internal                 */
  /* ------------------------------------------------------------------ */
  const renderFilters = () => (
    <SociollaCard style={styles.filterCard}>
      {/* Status filter — internal only; external is always "all" */}
      {!is3pl && (
        <View style={styles.filterRow}>
          <SociollaText style={styles.filterLabel}>Status:</SociollaText>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.statusScroll}
            contentContainerStyle={styles.pillsRow}>
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={() => onStatusPress('all')}
              style={[styles.pill, activeStatus === null && styles.pillActive]}>
              <NoTranslate>
                <SociollaText
                  style={[
                    styles.pillText,
                    activeStatus === null && styles.pillTextActive,
                  ]}>
                  All
                </SociollaText>
              </NoTranslate>
            </TouchableOpacity>
            {STATUSES.map((s) => {
              const isActive = activeStatus === s.id;
              return (
                <TouchableOpacity
                  key={s.id}
                  activeOpacity={0.8}
                  onPress={() => onStatusPress(s.id)}
                  style={[styles.pill, isActive && styles.pillActive]}>
                  <NoTranslate>
                    <SociollaText
                      style={[
                        styles.pillText,
                        isActive && styles.pillTextActive,
                      ]}>
                      {s.label}
                    </SociollaText>
                  </NoTranslate>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      )}

      {/* Period row */}
      <View style={styles.filterRow}>
        <SociollaText style={styles.filterLabel}>Period:</SociollaText>
        <View style={styles.periodRow}>
          <DatePicker
            hideLabel
            name="from_date"
            style={styles.datePicker}
            value={fromDate}
            onChange={(val) =>
              setFromDate(val ? formatISO(startOfDay(val)) : '')
            }
          />
          <SociollaText style={styles.dateSeparator}>→</SociollaText>
          <DatePicker
            hideLabel
            name="to_date"
            style={styles.datePicker}
            value={toDate}
            onChange={(val) => setToDate(val ? formatISO(endOfDay(val)) : '')}
          />
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={onApplyFilter}
            style={styles.applyBtn}>
            <SociollaText style={styles.applyBtnText}>Apply</SociollaText>
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={onResetFilter}
            style={styles.resetBtn}>
            <SociollaText style={styles.resetBtnText}>Reset</SociollaText>
          </TouchableOpacity>
        </View>
      </View>
    </SociollaCard>
  );

  /* ------------------------------------------------------------------ */
  /* Stat cards — horizontal scroll                                      */
  /* ------------------------------------------------------------------ */
  const renderStatCards = () => {
    const cards = [];

    if (!is3pl && activeStatus) {
      // Internal POV with a status selected: per-carrier cards in the selected status colour
      const statusColor =
        STATUSES.find((s) => s.id === activeStatus)?.color || '#242B38';
      (filteredData?.carrier_breakdown || []).forEach((c, idx) => {
        cards.push({
          key: `carrier-${idx}`,
          label: c._id?.carrier_name || c._id?.carrier_id || '-',
          value: c.count || 0,
          color: statusColor,
        });
      });
    } else {
      if (is3pl) {
        cards.push({
          key: 'total-customer',
          label: 'Total Customer',
          value: summary.total_customers || 0,
          color: TOTAL_CUSTOMER_COLOR,
        });
      }
      STATUSES.forEach((s) => {
        cards.push({
          key: `status-${s.id}`,
          id: s.id,
          label: `DO ${s.label}`,
          value: getStatusCount(s.id),
          color: s.color,
          // External POV: status cards redirect to the filtered GRN list
          clickable: is3pl,
        });
      });
    }

    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.statCardsRow}>
        {cards.map((card) => {
          const content = (
            <>
              <SociollaText style={styles.statCardLabel}>
                {card.label}
              </SociollaText>
              <SociollaText
                style={[styles.statCardValue, { color: card.color }]}>
                {card.value}
              </SociollaText>
            </>
          );
          return card.clickable ? (
            <TouchableOpacity
              key={card.key}
              activeOpacity={0.8}
              style={styles.statCard}
              onPress={() => goToGrnList(card.id)}>
              {content}
            </TouchableOpacity>
          ) : (
            <View key={card.key} style={styles.statCard}>
              {content}
            </View>
          );
        })}
      </ScrollView>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Carrier table — Internal POV only                                   */
  /* ------------------------------------------------------------------ */
  const renderCarrierTable = () => {
    // Period-bound (ignores the status filter) — table stays put on status change
    const carriers = summary?.carrier_table || [];
    if (!carriers.length) {
      return (
        <SociollaText style={styles.emptyText}>No carrier data</SociollaText>
      );
    }

    return (
      <ScrollView horizontal showsHorizontalScrollIndicator>
        <View style={styles.tableInner}>
          {/* Header row */}
          <View style={styles.tableRow}>
            <View
              style={[
                styles.tableCell,
                styles.tableCellCarrier,
                styles.tableHeader,
              ]}>
              <SociollaText style={styles.tableHeaderText}>
                Carrier
              </SociollaText>
            </View>
            {CARRIER_TABLE_COLS.map((col, colIdx) => (
              <View
                key={col.key}
                style={[
                  styles.tableCell,
                  styles.tableHeader,
                  colIdx === CARRIER_TABLE_COLS.length - 1 &&
                    styles.tableCellLast,
                ]}>
                {STATUSES.some((s) => s.id === col.key) ? (
                  <NoTranslate>
                    <SociollaText style={styles.tableHeaderText}>
                      {col.label}
                    </SociollaText>
                  </NoTranslate>
                ) : (
                  <SociollaText style={styles.tableHeaderText}>
                    {col.label}
                  </SociollaText>
                )}
              </View>
            ))}
          </View>

          {/* Data rows */}
          {carriers.map((item, idx) => {
            const altStyle = idx % 2 === 0 ? styles.tableRowAlt : null;
            return (
              <View key={idx} style={styles.tableRow}>
                <View
                  style={[styles.tableCell, styles.tableCellCarrier, altStyle]}>
                  <SociollaText style={styles.tableCellText}>
                    {item._id?.carrier_name || item._id?.carrier_id || '-'}
                  </SociollaText>
                </View>
                {CARRIER_TABLE_COLS.map((col, colIdx) => (
                  <View
                    key={col.key}
                    style={[
                      styles.tableCell,
                      altStyle,
                      colIdx === CARRIER_TABLE_COLS.length - 1 &&
                        styles.tableCellLast,
                    ]}>
                    <SociollaText
                      style={[styles.tableCellText, { color: col.color }]}>
                      {col.key === 'count'
                        ? `${item.count || 0} (${item.percentage || 0}%)`
                        : item[col.key] || 0}
                    </SociollaText>
                  </View>
                ))}
              </View>
            );
          })}
        </View>
      </ScrollView>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Pie legend — carriers (Internal) or statuses (External)            */
  /* ------------------------------------------------------------------ */
  const renderPieLegend = () => {
    let legendItems = [];

    if (is3pl) {
      const statusItems = summary?.status_breakdown || [];
      legendItems = statusItems.map((s, idx) => {
        const cfg = STATUSES.find((x) => x.id === s._id);
        return {
          key: idx,
          color: cfg ? cfg.color : '#ccc',
          name: cfg ? cfg.label : s._id,
          count: s.count || 0,
          pct: s.percentage || 0,
        };
      });
    } else {
      // Top 9 carriers only — matches pie chart data
      const carrierItems = (filteredData?.carrier_breakdown || []).slice(0, 9);
      legendItems = carrierItems.map((c, idx) => ({
        key: idx,
        color: CARRIER_COLORS[idx % CARRIER_COLORS.length],
        name: c._id?.carrier_name || c._id?.carrier_id || '-',
        count: c.count || 0,
        pct: c.percentage || 0,
      }));
    }

    return (
      <View style={styles.legendGrid}>
        {legendItems.map((item) => (
          <View key={item.key} style={styles.legendItem}>
            <View style={styles.legendNameRow}>
              <View
                style={[styles.legendSquare, { backgroundColor: item.color }]}
              />
              <SociollaText style={styles.legendName}>{item.name}</SociollaText>
            </View>
            <View style={styles.legendAmountRow}>
              <SociollaText style={styles.legendCount}>
                {item.count}
              </SociollaText>
              <SociollaText style={styles.legendPct}>
                ({item.pct}%)
              </SociollaText>
            </View>
          </View>
        ))}
      </View>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */
  return (
    <View style={styles.container}>
      <SociollaText type="medium" style={styles.pageTitle}>
        Goods Received Note
      </SociollaText>

      {renderFilters()}

      {renderStatCards()}

      {/* Pie chart + Legend */}
      {filterLoading ? (
        <View style={styles.loaderWrapper}>
          <Loader />
        </View>
      ) : (
        <>
          <SociollaCard style={styles.card}>
            {!is3pl && (
              <View style={styles.cardTitleRow}>
                <SociollaText type="medium" style={styles.cardTitle}>
                  {activeStatus ? 'Carrier' : 'Total Distribution by Carrier'}
                </SociollaText>
                {activeStatus ? (
                  <SociollaText style={styles.statusBadgeLabel}>
                    Status:{' '}
                    <SociollaText
                      style={[
                        styles.statusBadgeValue,
                        {
                          color: STATUSES.find((s) => s.id === activeStatus)
                            ?.color,
                        },
                      ]}>
                      {STATUSES.find((s) => s.id === activeStatus)?.label || ''}
                    </SociollaText>
                  </SociollaText>
                ) : null}
              </View>
            )}
            <View style={styles.pieRow}>
              <View style={styles.pieChartWrap}>
                <Highcharts
                  type="pie"
                  height={isWeb ? 300 : 240}
                  data={is3pl ? buildStatusPieData() : buildCarrierPieData()}
                  colors={is3pl ? pieColors : CARRIER_COLORS}
                  innerSize="50%"
                  showDataLabels={false}
                  pieTooltipFormat="{point.name} : <b>{point.y} ({point.percentage_value}%)</b>"
                />
              </View>
              <View style={styles.pieLegend}>{renderPieLegend()}</View>
            </View>
          </SociollaCard>

          {/* Carrier detail table — Internal POV only */}
          {!is3pl && (
            <SociollaCard style={styles.card}>
              <SociollaText type="medium" style={styles.cardTitle}>
                All Carrier Details
              </SociollaText>
              {renderCarrierTable()}
            </SociollaCard>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  pageTitle: {
    fontSize: isWeb ? 24 : 18,
    fontWeight: '700',
    marginBottom: 16,
    color: Colors.black || '#000',
  },
  filterCard: {
    padding: 16,
    marginBottom: 16,
  },
  filterRow: {
    flexDirection: isWeb ? 'row' : 'column',
    alignItems: isWeb ? 'center' : 'flex-start',
    marginBottom: 12,
    gap: isWeb ? 12 : 8,
  },
  filterLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.black || '#000',
    minWidth: 70,
  },
  statusScroll: {
    width: isWeb ? undefined : '100%',
  },
  pillsRow: {
    flexDirection: 'row',
    gap: 8,
    paddingVertical: 2,
  },
  pill: {
    paddingVertical: 5,
    paddingHorizontal: 14,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: Colors.grey0 || '#e0e0e0',
    backgroundColor: Colors.white || '#fff',
  },
  pillActive: {
    borderColor: Colors.blue0 || '#0070c0',
    backgroundColor: Colors.blue0 || '#0070c0',
  },
  pillText: {
    fontSize: 14,
    color: '#242B38',
  },
  pillTextActive: {
    color: Colors.white || '#fff',
  },
  periodRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  datePicker: {
    flex: isWeb ? 1 : undefined,
    minWidth: isWeb ? 130 : 100,
  },
  dateSeparator: {
    fontSize: 16,
    color: Colors.grey1,
    marginHorizontal: 4,
  },
  applyBtn: {
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 6,
    backgroundColor: Colors.blue0 || '#333BAF',
    justifyContent: 'center',
  },
  applyBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.white || '#fff',
  },
  resetBtn: {
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Colors.blue0 || '#333BAF',
    backgroundColor: Colors.white || '#fff',
    justifyContent: 'center',
  },
  resetBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.blue0 || '#333BAF',
  },
  statCardsRow: {
    flexDirection: 'row',
    gap: 12,
    paddingBottom: 4,
    marginBottom: 16,
  },
  statCard: {
    backgroundColor: Colors.white || '#fff',
    borderRadius: 8,
    paddingVertical: 16,
    paddingHorizontal: 20,
    alignItems: 'flex-start',
    minWidth: isWeb ? 140 : 120,
    borderWidth: 1,
    borderColor: Colors.grey0 || '#e0e0e0',
  },
  statCardLabel: {
    fontSize: isWeb ? 14 : 12,
    fontWeight: '600',
    color: '#3D454F',
    textAlign: 'left',
    marginBottom: 8,
  },
  statCardValue: {
    fontSize: isWeb ? 40 : 32,
    fontWeight: '600',
    lineHeight: isWeb ? 48 : 38,
  },
  card: {
    marginBottom: 16,
    padding: 16,
  },
  cardTitle: {
    fontSize: isWeb ? 20 : 16,
    fontWeight: '700',
    color: Colors.black || '#000',
    marginBottom: 16,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusBadgeLabel: {
    fontSize: isWeb ? 16 : 13,
    color: Colors.grey1,
    marginBottom: 16,
  },
  statusBadgeValue: {
    fontWeight: '700',
    color: Colors.black || '#000',
  },
  loaderWrapper: {
    minHeight: 120,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Table styles
  tableInner: {
    alignSelf: 'flex-start',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableRowAlt: {
    backgroundColor: '#F7F7F7',
  },
  tableHeader: {
    backgroundColor: '#293B77',
  },
  tableCell: {
    minWidth: isWeb ? 110 : 90,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderWidth: 0.5,
    borderColor: Colors.grey0 || '#e0e0e0',
    justifyContent: 'center',
  },
  tableCellCarrier: {
    width: isWeb ? 180 : 130,
  },
  tableCellLast: {
    borderRightWidth: 1,
    minWidth: isWeb ? 140 : 120,
  },
  tableHeaderText: {
    fontSize: isWeb ? 14 : 12,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'left',
  },
  tableCellText: {
    fontSize: isWeb ? 14 : 12,
    fontWeight: '600',
    color: '#242B38',
    textAlign: 'left',
  },
  emptyText: {
    fontSize: isWeb ? 13 : 12,
    color: Colors.grey1,
    textAlign: 'center',
    paddingVertical: 16,
  },
  // Pie chart + legend layout
  pieRow: {
    flexDirection: isWeb ? 'row' : 'column',
    alignItems: isWeb ? 'center' : 'stretch',
  },
  pieChartWrap: {
    width: isWeb ? '40%' : undefined,
  },
  pieLegend: {
    width: isWeb ? '60%' : undefined,
    paddingTop: isWeb ? 0 : 16,
    paddingLeft: isWeb ? 8 : 0,
  },
  legendGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  legendItem: {
    width: '33.33%',
    paddingRight: 12,
    marginBottom: 20,
  },
  legendNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  legendSquare: {
    width: 12,
    height: 12,
    borderRadius: 2,
    marginRight: 6,
    flexShrink: 0,
  },
  legendName: {
    fontSize: isWeb ? 14 : 12,
    color: '#7D828F',
    flexShrink: 1,
  },
  legendAmountRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 4,
  },
  legendCount: {
    fontSize: isWeb ? 24 : 18,
    fontWeight: '700',
    color: '#242B38',
  },
  legendPct: {
    fontSize: isWeb ? 16 : 13,
    color: '#7D828F',
  },
});

export default GRNDashboard;
